import { PayloadAction } from '@reduxjs/toolkit';
import Config from 'Cloud/config';
import { logger } from 'lib/logger';
import { DomainFoldersApiCall } from 'reactApp/api/DomainFoldersApiCall';
import { SearchApiCall } from 'reactApp/api/SearchApiCall';
import { VirusScanAPICall } from 'reactApp/api/VirusScanAPICall';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { isNewWebSearchBackendEnabled, isTouchSearchEnabled } from 'reactApp/appHelpers/featuresHelpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { getSlicedSearch } from 'reactApp/modules/file/utils';
import { isThisOrParentMounted } from 'reactApp/modules/home/home.selectors';
import { renameItemSuccess } from 'reactApp/modules/modifying/modifying.actions';
import { getNewItem } from 'reactApp/modules/modifying/modifying.helpers';
import { IRenameItemSuccess } from 'reactApp/modules/modifying/modifying.types';
import { IHistoryPush } from 'reactApp/modules/router/router.types';
import {
    searchLoadError,
    searchLoadMoreRequest,
    searchLoadMoreSuccess,
    searchLoadRequest,
    searchLoadRequestStart,
    searchLoadStart,
    searchLoadSuccess,
    searchRequestParamsUpdate,
    searchUpdateDomainFolders,
    searchUpdateVirusScan,
} from 'reactApp/modules/search/search.module';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { checkViewerAtIdChange, openMobileViewer as openMobileViewerAction } from 'reactApp/modules/viewer/viewer.module';
import { ESearchOptionType } from 'reactApp/ui/Header/Search/Search.data';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { decodeURIComponentHelper, getFilePathFromUrl } from 'reactApp/utils/urlHelper';
import { all, call, cancel, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { SettingsSelectors } from '../settings/settings.selectors';
import { getLoadMoreLimit } from '../storage/storage.helpers';
import { changeSearchHistory } from './search.actions';
import { sendSearchAnalytics, sendSearchStatistics, sendVirusScanStatistics } from './search.analytics';
import { getExtsBySearchType, SEARCH_PER_PAGE_LIMIT } from './search.helpers';
import { getSearchItemById, getSearchList, getSearchLoadMoreData, getSearchRequestParams } from './search.selectors';
import { ApiSearchResponceList, SearchApiRequestParams, SearchFile } from './search.types';

const DEPTH = 4294967295; // 2^32-1
const MAX_RESULT_COUNT = getLoadMoreLimit(EStorageType.search);

const searchApiCall = (params: SearchApiRequestParams) => new SearchApiCall().makeRequest(params);
const domainFoldersApiCall = () => new DomainFoldersApiCall().makeRequest();
const virusScanApiCall = ({ hash_list }) => new VirusScanAPICall().makeRequest({ hash_list });

/** Загружаем доменные папки, находим их в результатах поиска и помечаем. */
export function* searchModifyingDomainFolders() {
    try {
        const domainFolders = yield domainFoldersApiCall();
        const domainFoldersIDs = domainFolders?.data?.body?.map((item) => item.home) || [];

        if (domainFoldersIDs.length) {
            yield put(searchUpdateDomainFolders(domainFoldersIDs));
        }
    } catch (error) {
        logger.error(error);
    }
}

/** Проверяем результаты поиска на вирусы и измениям поля 'virus_scan', 'blocked' и 'illegal'. */
export function* searchModifyingVirusScan(results: ApiSearchResponceList, offset: number, total: number) {
    try {
        const hashList = results.reduce<string[]>((acc, cur) => {
            if ('hash' in cur && cur?.hash) {
                acc.push(cur?.hash);
            }
            return acc;
        }, []);

        let apiRequestCallDeltaT = performance.now();

        const { data, status } = yield virusScanApiCall({ hash_list: hashList });
        apiRequestCallDeltaT = performance.now() - apiRequestCallDeltaT;
        sendVirusScanStatistics(status, apiRequestCallDeltaT, offset, total);

        const virusScanResults = data?.body || {};

        if (Object.keys(virusScanResults).length) {
            yield put(searchUpdateVirusScan(virusScanResults));
        }
    } catch (error) {
        logger.error(error);
    }
}

function* loadSearch() {
    try {
        yield put(searchLoadRequest());

        const { query = '', searchType = ESearchOptionType.all } = yield select(SettingsSelectors.getQueryParams);
        const extensions = getExtsBySearchType(searchType);
        const searchQuery = query.trim();
        const id = searchQuery || ROOT_FOLDER_ID;
        const params: SearchApiRequestParams = {
            q: searchQuery,
            path: ROOT_FOLDER_ID,
            limit: MAX_RESULT_COUNT,
            depth: DEPTH,
        };

        if (isNewWebSearchBackendEnabled) {
            params.srch_exp = 1;
        }

        if (extensions) {
            params.ext = extensions;
        }
        // Магический привет от API. "type=d" означает искать по типу "папка".
        if (searchType === ESearchOptionType.folder) {
            params.type = 'd';
        }

        if (!extensions && searchType !== ESearchOptionType.folder && id === ROOT_FOLDER_ID) {
            return yield put(searchLoadSuccess());
        }

        let apiRequestCallDeltaT = performance.now();
        const { status, headers, data } = yield searchApiCall(params);
        apiRequestCallDeltaT = performance.now() - apiRequestCallDeltaT;

        const results = data?.body?.list || [];
        sendSearchStatistics(status, searchQuery, searchType, apiRequestCallDeltaT, results.length || 0);

        yield put(searchLoadStart(results || []));

        // Сохраняем параметры для дальнейшего использования в других местах приложения.
        const xPageId = Config.get('x-page-id') || 'empty_header';
        const xReqId = headers?.['x-req-id'] || 'empty_header';
        yield put(searchRequestParamsUpdate({ xPageId, xReqId, query: searchQuery }));

        sendSearchAnalytics(params, xPageId, xReqId);

        if (results.length) {
            yield call(searchModifyingDomainFolders);
            // Сначала сканируем первые SEARCH_PER_PAGE_LIMIT файлов.
            const virusScanList = results.slice(0, SEARCH_PER_PAGE_LIMIT);
            yield call(searchModifyingVirusScan, virusScanList, 0, SEARCH_PER_PAGE_LIMIT);
        }
        yield put(searchLoadSuccess());

        const viewerItemId = decodeURIComponentHelper(getFilePathFromUrl(location?.pathname));

        if (isTouchSearchEnabled && viewerItemId && IS_PHONE_BROWSER) {
            yield put(openMobileViewerAction({ id: viewerItemId }));
        }
    } catch (error) {
        logger.error(error);
        yield put(searchLoadError());
    }
}

function* loadMoreSearch() {
    try {
        const { offset, list } = yield select(getSearchLoadMoreData);
        const nextResultList = list?.slice(offset, offset + SEARCH_PER_PAGE_LIMIT) || [];
        yield call(searchModifyingVirusScan, nextResultList, offset, list.length);

        yield put(searchLoadMoreSuccess(nextResultList.length));
    } catch (error) {
        logger.error(error);
        yield cancel();
    }
}

function* updateRenamedItems(id: string, newFolderId: string, oldFolderId: string) {
    const item: CloudItem = yield select((state) => getSearchItemById(state, id));

    if (!item) {
        return;
    }

    const newId = id.replace(oldFolderId, newFolderId);
    const isMounted: boolean = yield select(isThisOrParentMounted, item);
    const newItem: CloudItem = getNewItem({ item, newId, isMounted });

    yield put(renameItemSuccess({ newItem, oldId: item.id }));
}

// test
// -- test.jpg
// При переименовании папки test надо обновить test.jpg
function* handleRenameItems(action: PayloadAction<IRenameItemSuccess>) {
    const { newItem, oldId } = action.payload;

    const item = newItem as SearchFile;

    const { xPageId, xReqId, query } = yield select(getSearchRequestParams);
    sendPaymentGa({
        eventCategory: ECategoryGa.search_results,
        action: 'rename',
        id_media: item.id,
        size_file: item.size,
        source: 'search',
        section: 'search',
        name_files: item.name,
        type_content: item.kind,
        search_phrase: query,
        page_id: xPageId,
        req_id: xReqId,
        pos: item.pos,
        hash: item.hash,
    });

    if (!newItem.isFolder) {
        return;
    }

    const searchList: string[] | undefined = yield select(getSearchList);
    const idsToUpdate = searchList?.filter((item) => item?.indexOf(`${oldId}/`) === 0);

    if (!idsToUpdate?.length) {
        return;
    }

    yield all(idsToUpdate.map((id: string) => updateRenamedItems(id, newItem.id, oldId)));
}

function* changeSearchHistorySaga(action: PayloadAction<IHistoryPush>) {
    if (!isTouchSearchEnabled || !IS_PHONE_BROWSER) {
        return;
    }

    const { id } = action.payload;

    let idFixed;
    try {
        idFixed = decodeURIComponent(getSlicedSearch(id));
    } catch (error) {
        idFixed = getSlicedSearch(id);
        logger.error(error);
    }
    const item = yield select(getSearchItemById, idFixed);

    yield put(checkViewerAtIdChange({ id: item?.id, storage: EStorageType.search }));
}

export function* watchSearch() {
    yield takeEvery(searchLoadRequestStart.toString(), loadSearch);
    yield takeEvery(searchLoadMoreRequest.toString(), loadMoreSearch);
    yield takeEvery(renameItemSuccess.toString(), handleRenameItems);
    yield takeLatest(changeSearchHistory.toString(), changeSearchHistorySaga);
}
